import React, { type FC } from "react";

import { IconCloseCircleFilled } from "~/components/icon";
import { type VariantProps, cv } from "~/tools/class-variance";

import { useExplorationDrawerState } from "../drawer/use-exploration-drawer-state";
import { Link } from "../link";

const variants = cv("flex grow py-2 font-light text-sm/4", {
	variants: {
		color: {
			light: "text-white",
			dark: "text-gray-900",
		},
		justify: {
			start: "justify-start",
			center: "justify-center",
		},
	},

	defaultVariants: {
		color: "dark",
		justify: "center",
	},
});

export interface GenericErrorMessageProps extends VariantProps<typeof variants> {
	className?: string;
	error: unknown;
	onRetry?: () => void; // display error button if onRetry is specified
	text?: string; // full text message
	highlightText?: string; // a single word from a @text property to highlight, on click displays the @error
}

/**
 * Use this component to display error messages with a link to details, but only within views and drawers.
 *
 * Do NOT use this component inside `notification` popups!
 * `notification` popups do not support React context, which will lead to errors.
 * If you need to show error details in a notification, use the `useErrorMessage` hook instead.
 */
export const GenericErrorMessage: FC<GenericErrorMessageProps> = ({
	className,
	error,
	onRetry,
	text = "Something went wrong.",
	highlightText = "Something",
	color = "dark",
	justify,
}) => {
	const explorationDrawer = useExplorationDrawerState();

	const textFragment: Array<string | React.ReactElement> = text.split(" ");
	const index = textFragment.indexOf(highlightText);
	if (index >= 0) {
		textFragment[index] = (
			<Link
				className="font-light underline"
				data-testid="generic-error-message-show-more"
				el="button"
				onClick={() => explorationDrawer.openErrorDetails({ error })}
				theme={color === "light" ? "light" : "unstyled"}
				type="button"
			>
				{highlightText}
			</Link>
		);
	}

	return (
		<div className={variants({ className, color, justify })} data-testid="generic-error-message">
			<div className="flex flex-wrap items-center justify-center self-center">
				<IconCloseCircleFilled className="mr-2.5 inline-flex text-base text-red-500" />
				{textFragment.map((el, index) => (
					<React.Fragment key={index}>{el}&nbsp;</React.Fragment>
				))}
				{onRetry && (
					<Link
						className="font-light underline"
						data-testid="generic-error-message-retry-button"
						el="button"
						onClick={onRetry}
						theme={color === "light" ? "light" : "unstyled"}
						type="button"
					>
						Retry
					</Link>
				)}
			</div>
		</div>
	);
};
